import { useMemo, useState } from "react";
import { Download, FileSpreadsheet, Upload } from "lucide-react";
import { readSheet } from "read-excel-file/browser";

import { Table } from "@/components/data-table";
import { FieldError } from "@/components/field-error";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { actions } from "@/lib/api";
import type {
  ImportKind,
  ImportPreview,
  ImportResult,
} from "@/lib/types";

const ROW_CAP = 1000;

type TargetField = {
  key: string;
  label: string;
  required: boolean;
  aliases: string[];
};

const SUPPLIER_FIELDS: TargetField[] = [
  { key: "name", label: "Name", required: true, aliases: ["supplier", "supplier name", "vendor", "vendor name", "legal name"] },
  { key: "contact_name", label: "Contact", required: false, aliases: ["contact", "contact person", "contact name"] },
  { key: "email", label: "Email", required: true, aliases: ["email", "email address", "quote email"] },
  { key: "phone", label: "Phone", required: false, aliases: ["phone", "phone number", "mobile"] },
  { key: "address", label: "Address", required: false, aliases: ["address", "registered address"] },
  { key: "tax_identifier", label: "Tax identifier", required: false, aliases: ["tax identifier", "tax id", "gstin", "vat number"] },
  { key: "country_code", label: "Country code", required: true, aliases: ["country", "country code"] },
  { key: "currency", label: "Currency", required: true, aliases: ["currency", "default currency"] },
  { key: "payment_terms", label: "Payment terms (days)", required: true, aliases: ["payment terms", "payment terms days", "terms", "terms days"] },
  { key: "category", label: "Category", required: false, aliases: ["category", "supplier category", "vendor category"] },
];

const INVENTORY_FIELDS: TargetField[] = [
  { key: "code", label: "Code", required: true, aliases: ["code", "sku", "item code"] },
  { key: "description", label: "Description", required: true, aliases: ["description", "item", "item name", "name"] },
  { key: "specification", label: "Specification", required: true, aliases: ["specification", "specifications", "spec"] },
  { key: "unit", label: "Unit", required: true, aliases: ["unit", "uom", "unit of measure"] },
  { key: "category", label: "Category", required: true, aliases: ["category", "item category"] },
  { key: "location", label: "Location", required: true, aliases: ["location", "site", "branch"] },
  { key: "opening_stock", label: "Opening stock", required: false, aliases: ["opening stock", "stock", "on hand", "on_hand"] },
  { key: "reorder_point", label: "Reorder point", required: false, aliases: ["reorder point", "minimum stock", "min stock"] },
  { key: "target_stock", label: "Target stock", required: false, aliases: ["target stock", "maximum stock", "max stock"] },
];

const CONTROL_MATRIX_FIELDS: TargetField[] = [
  { key: "rule_code", label: "Rule code", required: true, aliases: ["rule", "control id", "control code"] },
  { key: "control_type", label: "Control type", required: true, aliases: ["type", "control"] },
  { key: "priority", label: "Priority", required: false, aliases: ["precedence", "order"] },
  { key: "amount_min", label: "Amount minimum", required: false, aliases: ["min amount", "minimum", "threshold"] },
  { key: "amount_max", label: "Amount maximum", required: false, aliases: ["max amount", "maximum"] },
  { key: "category", label: "Category", required: false, aliases: ["spend category", "cost category"] },
  { key: "purchase_kind", label: "Purchase kind", required: false, aliases: ["purchase type"] },
  { key: "purchase_classification", label: "Classification", required: false, aliases: ["purchase classification"] },
  { key: "required_quotation_count", label: "Required quotations", required: false, aliases: ["quote count", "quotations"] },
  { key: "required_approval_count", label: "Required approvals", required: false, aliases: ["approval count", "approvals"] },
  { key: "enforcement", label: "Enforcement", required: true, aliases: ["mode", "control mode"] },
  { key: "non_l1_mode", label: "Non-L1 mode", required: false, aliases: ["non l1", "l1 bypass"] },
  { key: "approver_roles", label: "Approver roles", required: false, aliases: ["approval roles"] },
  { key: "waiver_approver_roles", label: "Waiver approver roles", required: false, aliases: ["waiver roles"] },
  { key: "waiver_validity_days", label: "Waiver validity days", required: false, aliases: ["waiver days"] },
];

const HISTORICAL_FIELDS: TargetField[] = [
  { key: "external_record_id", label: "External record ID", required: true, aliases: ["external id", "legacy id", "record id"] },
  { key: "record_type", label: "Record type", required: true, aliases: ["type", "document type"] },
  { key: "occurred_at", label: "Occurred at", required: true, aliases: ["date", "transaction date", "occurred"] },
  { key: "title", label: "Title", required: true, aliases: ["name", "subject"] },
  { key: "description", label: "Description", required: false, aliases: ["details", "notes"] },
  { key: "category", label: "Category", required: false, aliases: ["spend category", "cost category"] },
  { key: "supplier_name", label: "Supplier name", required: false, aliases: ["supplier", "vendor"] },
  { key: "amount", label: "Amount", required: false, aliases: ["value", "total"] },
  { key: "currency", label: "Currency", required: false, aliases: ["currency code"] },
  { key: "legacy_status", label: "Legacy status", required: false, aliases: ["status"] },
  { key: "provenance_label", label: "Provenance label", required: true, aliases: ["provenance", "origin"] },
  { key: "source_reference", label: "Source reference", required: true, aliases: ["source", "evidence reference"] },
  { key: "evidence", label: "Evidence JSON", required: false, aliases: ["evidence", "metadata"] },
];

const IMPORT_FIELDS: Record<ImportKind, TargetField[]> = {
  suppliers: SUPPLIER_FIELDS,
  inventory: INVENTORY_FIELDS,
  control_matrix: CONTROL_MATRIX_FIELDS,
  historical_procurement: HISTORICAL_FIELDS,
};

const IMPORT_TITLES: Record<ImportKind, string> = {
  suppliers: "Import suppliers",
  inventory: "Import inventory items",
  control_matrix: "Import control matrix",
  historical_procurement: "Import historical procurement evidence",
};

type SourceRow = {
  rowNumber: number;
  values: string[];
};

type DuplicateAction = "skip" | "update";

function normalizeHeader(value: string) {
  return value.trim().toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ");
}

function cellText(value: unknown) {
  if (value === null || value === undefined) return "";
  if (value instanceof Date) return value.toISOString();
  return String(value).trim();
}

function parseCsv(content: string) {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let index = 0; index < content.length; index += 1) {
    const character = content[index];
    if (quoted) {
      if (character === '"' && content[index + 1] === '"') {
        cell += '"';
        index += 1;
      } else if (character === '"') quoted = false;
      else cell += character;
    } else if (character === '"') quoted = true;
    else if (character === ",") {
      row.push(cell);
      cell = "";
    } else if (character === "\n") {
      row.push(cell.replace(/\r$/, ""));
      rows.push(row);
      row = [];
      cell = "";
    } else cell += character;
  }
  row.push(cell.replace(/\r$/, ""));
  if (row.some((value) => value.length > 0)) rows.push(row);
  if (quoted) throw new Error("The CSV contains an unclosed quoted cell.");
  return rows;
}

async function readSpreadsheet(file: File) {
  const extension = file.name.split(".").pop()?.toLowerCase();
  if (extension === "csv") return parseCsv(await file.text());
  if (extension === "xlsx") {
    const rows = await readSheet(file);
    return rows.map((row) => row.map(cellText));
  }
  throw new Error("Choose a .xlsx or .csv file.");
}

function csvCell(value: unknown) {
  const text = String(value ?? "");
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function downloadCsv(fileName: string, rows: unknown[][]) {
  const content = rows.map((row) => row.map(csvCell).join(",")).join("\r\n");
  const url = URL.createObjectURL(new Blob([content], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

async function sha256File(file: File) {
  const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

function defaultMapping(headers: string[], fields: TargetField[]) {
  const normalized = headers.map(normalizeHeader);
  return Object.fromEntries(
    fields.map((field) => {
      const index = normalized.findIndex(
        (header) =>
          header === normalizeHeader(field.label) ||
          field.aliases.some((alias) => header === normalizeHeader(alias)),
      );
      return [field.key, index >= 0 ? String(index) : ""];
    }),
  );
}

export function ImportWizard({
  open,
  onOpenChange,
  organisationId,
  importKind,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organisationId: string;
  importKind: ImportKind;
  onDone: () => void;
}) {
  const fields = IMPORT_FIELDS[importKind];
  const title = IMPORT_TITLES[importKind];
  const governedImport = importKind === "control_matrix" || importKind === "historical_procurement";
  const [file, setFile] = useState<File>();
  const [headers, setHeaders] = useState<string[]>([]);
  const [sourceRows, setSourceRows] = useState<SourceRow[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<ImportPreview>();
  const [duplicateActions, setDuplicateActions] = useState<Record<number, DuplicateAction>>({});
  const [result, setResult] = useState<ImportResult>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sourceSystem, setSourceSystem] = useState("");
  const [mappingVersion, setMappingVersion] = useState("raqoz-control-mapping-v1");
  const [policyName, setPolicyName] = useState("");
  const [rationale, setRationale] = useState("");

  const mappedRows = useMemo(
    () =>
      sourceRows.map((row) => ({
        row_number: row.rowNumber,
        values: Object.fromEntries(
          fields.map((field) => {
            const sourceIndex = mapping[field.key];
            return [
              field.key,
              sourceIndex === "" || sourceIndex === undefined
                ? ""
                : row.values[Number(sourceIndex)] || "",
            ];
          }),
        ),
      })),
    [fields, mapping, sourceRows],
  );

  function reset() {
    setFile(undefined);
    setHeaders([]);
    setSourceRows([]);
    setMapping({});
    setPreview(undefined);
    setDuplicateActions({});
    setResult(undefined);
    setBusy(false);
    setError("");
    setSourceSystem("");
    setMappingVersion("raqoz-control-mapping-v1");
    setPolicyName("");
    setRationale("");
  }

  function close(nextOpen: boolean) {
    if (!nextOpen) reset();
    onOpenChange(nextOpen);
  }

  async function acceptFile(selected?: File) {
    if (!selected) return;
    setBusy(true);
    setError("");
    setPreview(undefined);
    setResult(undefined);
    try {
      const rows = await readSpreadsheet(selected);
      if (rows.length < 2) throw new Error("The file must contain a header row and at least one data row.");
      const detectedHeaders = rows[0].map(cellText);
      if (detectedHeaders.some((header) => !header)) throw new Error("Every source column must have a header.");
      const dataRows = rows.slice(1).map((values, index) => ({
        rowNumber: index + 2,
        values: values.map(cellText),
      }));
      if (dataRows.length > ROW_CAP) throw new Error(`Imports are limited to ${ROW_CAP} rows. Split this file and try again.`);
      setFile(selected);
      setHeaders(detectedHeaders);
      setSourceRows(dataRows);
      setMapping(defaultMapping(detectedHeaders, fields));
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function validate() {
    const unmapped = fields.filter((field) => field.required && mapping[field.key] === "");
    if (unmapped.length) {
      setError(`Map the required fields: ${unmapped.map((field) => field.label).join(", ")}.`);
      return;
    }
    setBusy(true);
    setError("");
    try {
      const nextPreview = await actions.previewDataImport(
        organisationId,
        importKind,
        mappedRows,
      );
      setPreview(nextPreview);
      setDuplicateActions(
        Object.fromEntries(
          nextPreview.rows
            .filter((row) => row.duplicate_id)
            .map((row) => [row.row_number, "skip"]),
        ),
      );
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function applyBulk(action: DuplicateAction) {
    if (!preview) return;
    setDuplicateActions(
      Object.fromEntries(
        preview.rows
          .filter((row) => row.duplicate_id)
          .map((row) => [row.row_number, action]),
      ),
    );
  }

  async function commit() {
    if (!file || !preview || !preview.valid) return;
    if (governedImport && (!sourceSystem.trim() || !mappingVersion.trim())) {
      setError("Source system and mapping version are required for governed imports.");
      return;
    }
    if (importKind === "control_matrix" && (!policyName.trim() || rationale.trim().length < 10)) {
      setError("Policy name and a change rationale of at least 10 characters are required.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const nextResult = await actions.commitDataImport(
        organisationId,
        importKind,
        file.name,
        mappedRows.map((row) => ({
          ...row,
          action: preview.rows.find((item) => item.row_number === row.row_number)?.duplicate_id
            ? duplicateActions[row.row_number] || "skip"
            : "create",
        })),
        governedImport
          ? {
              fileSha256: await sha256File(file),
              mappingVersion: mappingVersion.trim(),
              sourceSystem: sourceSystem.trim(),
              policyName: policyName.trim(),
              rationale: rationale.trim(),
            }
          : undefined,
      );
      setResult(nextResult);
      if (nextResult.failed === 0) onDone();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function downloadTemplate() {
    downloadCsv(
      `raqoz-${importKind.replaceAll("_", "-")}-import-template.csv`,
      [fields.map((field) => field.key)],
    );
  }

  function downloadErrors() {
    const errorsByRow = new Map<number, string>();
    if (preview) {
      for (const row of preview.rows) {
        if (row.errors.length) errorsByRow.set(row.row_number, row.errors.join("; "));
      }
    }
    if (result?.errors.length) {
      for (const item of result.errors) {
        if ("row_number" in item) errorsByRow.set(item.row_number, item.errors.join("; "));
        else for (const row of sourceRows) errorsByRow.set(row.rowNumber, item.message);
      }
    }
    downloadCsv(
      `raqoz-${importKind}-import-errors.csv`,
      [
        ["original_row_number", ...headers, "errors"],
        ...sourceRows
          .filter((row) => errorsByRow.has(row.rowNumber))
          .map((row) => [row.rowNumber, ...row.values, errorsByRow.get(row.rowNumber)]),
      ],
    );
  }

  const validationErrors = preview?.rows.filter((row) => row.errors.length) || [];
  const duplicateCount = preview?.rows.filter((row) => row.duplicate_id).length || 0;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="w-[min(1120px,calc(100vw-2rem))]">
        <DialogHeader
          eyebrow="DATA IMPORT"
          title={title}
          description={`Upload up to ${ROW_CAP.toLocaleString()} rows. Validation completes before any record is written.`}
        />
        <div className="grid gap-6 p-6">
          <div className="flex flex-wrap items-center gap-3">
            <Button className="min-h-11" type="button" variant="outline" onClick={downloadTemplate}>
              <Download className="size-4" />
              Download template
            </Button>
            <Label className="min-w-64 flex-1">
              Upload .xlsx or .csv
              <Input
                className="min-h-11"
                type="file"
                accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                disabled={busy}
                onChange={(event) => void acceptFile(event.target.files?.[0])}
              />
            </Label>
          </div>

          {file && !result && (
            <section aria-labelledby="column-mapping-title">
              <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
                <div>
                  <h3 id="column-mapping-title" className="text-lg font-semibold text-[var(--text-primary)]">Column mapping</h3>
                  <p className="text-sm text-[var(--text-secondary)]">
                    <FileSpreadsheet className="mr-1 inline size-4" />
                    {file.name} · <span className="numeric">{sourceRows.length}</span> rows
                  </p>
                </div>
                <Button className="min-h-11" type="button" disabled={busy} onClick={() => void validate()}>
                  Validate preview
                </Button>
              </div>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {fields.map((field) => (
                  <Label key={field.key}>
                    {field.label}{field.required ? " · Required" : " · Optional"}
                    <Select
                      className="min-h-11"
                      value={mapping[field.key] || ""}
                      onChange={(event) => {
                        setMapping((current) => ({ ...current, [field.key]: event.target.value }));
                        setPreview(undefined);
                      }}
                    >
                      <option value="">Not mapped</option>
                      {headers.map((header, index) => (
                        <option key={`${header}-${index}`} value={index}>{header}</option>
                      ))}
                    </Select>
                  </Label>
                ))}
              </div>
              {governedImport && (
                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                  <Label>
                    Source system · Required
                    <Input
                      className="min-h-11"
                      value={sourceSystem}
                      onChange={(event) => setSourceSystem(event.target.value)}
                      placeholder="Legacy ERP or policy register"
                    />
                  </Label>
                  <Label>
                    Mapping version · Required
                    <Input
                      className="min-h-11"
                      value={mappingVersion}
                      onChange={(event) => setMappingVersion(event.target.value)}
                    />
                  </Label>
                  {importKind === "control_matrix" && (
                    <>
                      <Label>
                        Draft policy name · Required
                        <Input
                          className="min-h-11"
                          value={policyName}
                          onChange={(event) => setPolicyName(event.target.value)}
                        />
                      </Label>
                      <Label>
                        Change rationale · Required
                        <Textarea
                          value={rationale}
                          minLength={10}
                          onChange={(event) => setRationale(event.target.value)}
                          placeholder="Explain why this matrix is being introduced."
                        />
                      </Label>
                    </>
                  )}
                </div>
              )}
            </section>
          )}

          {preview && !result && (
            <section aria-labelledby="validation-preview-title">
              <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
                <div>
                  <h3 id="validation-preview-title" className="text-lg font-semibold text-[var(--text-primary)]">Validation preview</h3>
                  <p className="text-sm text-[var(--text-secondary)]">
                    Every source row is shown below. Nothing has been written.
                  </p>
                </div>
                {duplicateCount > 0 && (
                  <Label>
                    Default for <span className="numeric">{duplicateCount}</span> duplicates
                    <Select className="min-h-11" defaultValue="skip" onChange={(event) => applyBulk(event.target.value as DuplicateAction)}>
                      <option value="skip">Skip existing records</option>
                      <option value="update">Update existing records</option>
                    </Select>
                  </Label>
                )}
              </div>
              <Table heads={["Source row", "Mapped values", "Validation", "Duplicate handling"]}>
                {preview.rows.map((row) => (
                  <tr key={row.row_number}>
                    <td className="numeric">{row.row_number}</td>
                    <td>
                      {fields.map((field) => `${field.label}: ${row.values[field.key] || "—"}`).join(" · ")}
                    </td>
                    <td>
                      {row.errors.length ? (
                        <ul className="list-disc pl-4 text-[var(--error)]">
                          {row.errors.map((message) => <li key={message}>{message}</li>)}
                        </ul>
                      ) : (
                        <span>Valid</span>
                      )}
                    </td>
                    <td>
                      {row.duplicate_id ? (
                        <Label>
                          Matches {row.duplicate_label}
                          <Select
                            className="min-h-11"
                            value={duplicateActions[row.row_number] || "skip"}
                            onChange={(event) => setDuplicateActions((current) => ({
                              ...current,
                              [row.row_number]: event.target.value as DuplicateAction,
                            }))}
                          >
                            <option value="skip">Skip</option>
                            <option value="update">Update</option>
                          </Select>
                        </Label>
                      ) : "Create"}
                    </td>
                  </tr>
                ))}
              </Table>
              <div className="mt-4 flex flex-wrap justify-end gap-3">
                {validationErrors.length > 0 && (
                  <Button className="min-h-11" type="button" variant="outline" onClick={downloadErrors}>
                    <Download className="size-4" />
                    Download error report
                  </Button>
                )}
                <Button className="min-h-11" type="button" disabled={busy || !preview.valid} onClick={() => void commit()}>
                  <Upload className="size-4" />
                  Commit import
                </Button>
              </div>
            </section>
          )}

          {result && (
            <section aria-labelledby="import-result-title">
              <h3 id="import-result-title" className="text-lg font-semibold text-[var(--text-primary)]">Import result</h3>
              <div className="mt-3 grid gap-3 sm:grid-cols-4">
                {(["created", "updated", "skipped", "failed"] as const).map((key) => (
                  <div key={key} className="rounded-lg border border-[var(--border)] bg-[var(--surface)] p-4">
                    <span className="block text-sm capitalize text-[var(--text-secondary)]">{key}</span>
                    <strong className="numeric text-2xl text-[var(--text-primary)]">{result[key]}</strong>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex flex-wrap justify-end gap-3">
                {result.failed > 0 && (
                  <Button className="min-h-11" type="button" variant="outline" onClick={downloadErrors}>
                    <Download className="size-4" />
                    Download error report
                  </Button>
                )}
                <Button className="min-h-11" type="button" onClick={() => close(false)}>Close</Button>
              </div>
            </section>
          )}

          <FieldError>{error}</FieldError>
        </div>
      </DialogContent>
    </Dialog>
  );
}
